import { useEffect, useMemo, useState } from "react";
import { List, ListItem, ListItemText, MenuItem, Paper, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import TzZoneData from "tzdata";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import useFrequencyTexts from "../useFrequencyTexts";
import { checkInputValue } from "../../../../utils/utilities";
import { BookingType, Frequence } from "../../../../features/Booking-Form/typings/booking-inputs";
import MsTeamsCallSwitch from "../../msTeams-call-switch.component";
import { FormConfirmConferenceServices } from "./FormConfirmConferenceServices.partial";
import { UserOrEmailMapping } from "../../../../features/Admin/typings/admin.types";
import {
  checkModeVehicle,
  checkModeResource
} from "../../../../features/Booking-Form/functions/booking-form.functions";
import { buildTeamBookingConfirmSummary } from "./form-confirm-card-team-confirm.functions";

interface Props {
  /** user can change the time tone when mobile working is active */
  onChangeTimezone: (newTimezone: string) => unknown;
}

export default function FormConfirmCard({ onChangeTimezone }: Readonly<Props>) {
  const { t } = useTranslation();
  const { timeFormat, dateFormat } = useTimeFormat();
  const { generate } = useFrequencyTexts();

  const {
    userInformation: {
      firstName,
      surname,
      msGraphEnabled,
      company: {
        meta: { calendarSyncPossible }
      }
    },
    settings: { timezone }
  } = useSelector((state: RootState) => state.login);
  const { inputs } = useSelector((state: RootState) => state.booking);

  const [primary, secondary] = useMemo(() => generate(inputs), [inputs, generate]);
  const [timezoneMobileWorking, setTimezoneMobileWorking] = useState<string>(timezone);
  const timezones = Object.keys(TzZoneData.zones).filter(
    tz => DateTime.local().setZone(tz).isValid
  );

  const isMsTeamsCall =
    calendarSyncPossible && msGraphEnabled && inputs.bookingType === BookingType.CONFERENCEZONE;

  const isVehicleSingleRangeBooking =
    checkModeVehicle(inputs) && inputs.frequence === Frequence.SINGLE;

  const handleMemberText = (member: UserOrEmailMapping) => {
    const firstNamePart =
      member.firstName === "external" ? t("_visitorFirstNameOnly") : member.firstName;
    const surnamePart = member.external ? "" : member.surname?.concat(" ");
    return `${firstNamePart} ${surnamePart} (${member.email})`;
  };

  // communicate the time zone change to outside components
  useEffect(() => {
    onChangeTimezone(timezoneMobileWorking);
  }, [timezoneMobileWorking]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        padding: 2,
        marginTop: 1,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: theme => theme.palette.background.paper
      }}
    >
      <List aria-label="secondary mailbox folders">
        {/* Name and team information */}
        <ListItem>
          <ListItemText
            data-testid="confirm-booking-user"
            primary={t("Name")}
            secondary={firstName + " " + surname}
          />
        </ListItem>

        {/* team booking - chosen team members list and visitors list */}
        {inputs.mode === BookingType.TEAM && (
          <ListItem>
            <ListItemText
              data-testid="confirm-booking-team"
              primary={t("Chosen Team")}
              slotProps={{ secondary: { sx: { whiteSpace: "pre-line" } } }}
              secondary={buildTeamBookingConfirmSummary(inputs.usersBookedFor, t)}
            />
          </ListItem>
        )}

        {/* conference zone or normal zone booking - chose participants list */}
        {(inputs.mode === BookingType.CONFERENCEZONE || inputs.mode === BookingType.PLACEZONE) &&
          inputs.zoneAccess &&
          inputs.zoneAccess.length > 0 && (
            <ListItem>
              <ListItemText
                primary={t("Participants")}
                slotProps={{ secondary: { sx: { whiteSpace: "pre-line" } } }}
                secondary={
                  inputs.zoneAccess
                    ? inputs.zoneAccess.map(member => handleMemberText(member)).join(", \n")
                    : t("Not set")
                }
              />
            </ListItem>
          )}

        {/* conference zone booking - invited visitors (visit-only) */}
        {inputs.mode === BookingType.CONFERENCEZONE &&
          (inputs.visitorRegistrations?.length ?? 0) > 0 && (
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-conference-visitors"
                primary={t("Visitors")}
                slotProps={{ secondary: { sx: { whiteSpace: "pre-line" } } }}
                secondary={(inputs.visitorRegistrations ?? [])
                  .map(v => `${v.firstName} ${v.surname} (${v.email})`)
                  .join(", \n")}
              />
            </ListItem>
          )}

        {/* Location information */}
        {!inputs.isMobileWorking && inputs.mode !== BookingType.VISITOR && (
          <ListItem>
            <ListItemText
              data-testid="confirm-booking-location"
              primary={t("Location")}
              slotProps={{ secondary: { sx: { whiteSpace: "pre-line" } } }}
              secondary={
                checkModeVehicle(inputs)
                  ? inputs.bookedVehicle?.location?.name ?? inputs.locationName
                  : checkModeResource(inputs)
                    ? inputs.bookedResource?.locationName ?? inputs.locationName
                    : inputs.locationName
              }
            />
          </ListItem>
        )}

        {/* conference zone booking - service order information */}
        {inputs.mode === BookingType.CONFERENCEZONE && (
          <FormConfirmConferenceServices inputs={inputs} />
        )}

        {isVehicleSingleRangeBooking && (
          <>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-date-start"
                primary={t("Start Date")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingFrom)).toFormat(
                  dateFormat
                )}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                data-testid="confirm-booking-time-start"
                primary={t("Start Time")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingStart)).toFormat(
                  timeFormat
                )}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-date-end"
                primary={t("End Date")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingTo)).toFormat(dateFormat)}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-time-end"
                primary={t("End Time")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingEnd)).toFormat(
                  timeFormat
                )}
              />
            </ListItem>
          </>
        )}

        {/* Day and date information */}
        {!isVehicleSingleRangeBooking && !inputs.specificDays && (
          <>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-date-start"
                primary={t("Start Date")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingFrom)).toFormat(
                  dateFormat
                )}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                data-testid="confirm-booking-date-end"
                primary={t("End Date")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingTo)).toFormat(dateFormat)}
              />
            </ListItem>
          </>
        )}

        {/* Weekdays information */}
        {inputs.weekdays &&
          inputs.frequence === 2 &&
          Object.values(inputs.weekdays).find(Boolean) && (
            <ListItem>
              <ListItemText
                primary={t("Weekdays")}
                secondary={
                  inputs.weekdays
                    ? Object.keys(inputs.weekdays)
                        //@ts-ignore
                        .filter(k => inputs.weekdays[k])
                        //translate the weekdays
                        .map(k => t(k))
                        .join(", ")
                    : t("Not set")
                }
              />
            </ListItem>
          )}

        {/* deleted - Excluded days information */}

        {/* Repeat information */}
        {!isVehicleSingleRangeBooking && (
          <ListItem>
            <ListItemText
              data-testid="confirm-booking-specific-days"
              primary={primary}
              secondary={secondary}
            />
          </ListItem>
        )}

        {/* Time information */}
        {!isVehicleSingleRangeBooking && inputs.mode !== BookingType.PLACEZONE && (
          <>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-time-start"
                primary={t("Start Time")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingStart)).toFormat(
                  timeFormat
                )}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                data-testid="confirm-booking-time-end"
                primary={t("End Time")}
                secondary={DateTime.fromISO(checkInputValue(inputs.bookingEnd)).toFormat(
                  timeFormat
                )}
              />
            </ListItem>
          </>
        )}

        {/** A text field to allow switching the time zone */}
        {inputs.isMobileWorking && (
          <ListItem>
            <TextField
              value={timezoneMobileWorking || ""}
              select
              fullWidth
              label={t("Timezone")}
              onChange={e => setTimezoneMobileWorking(e.target.value)}
            >
              {timezones.map(z => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </TextField>
          </ListItem>
        )}

        {/* MS Teams information */}
        {isMsTeamsCall && (
          <ListItem>
            <MsTeamsCallSwitch enableEdit={false} />
          </ListItem>
        )}
      </List>
    </Paper>
  );
}
